import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Wifi, Tv, Coffee, Wind, Check } from "lucide-react";
import axios from "axios";
import Header from "@/components/Header";
import GuestyWidget from "@/components/GuestyWidget";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PropertyDetails = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProperty();
  }, [id]);

  const fetchProperty = async () => {
    try {
      const response = await axios.get(`${API}/properties/${id}`);
      setProperty(response.data);
    } catch (error) {
      console.error("Error fetching property:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-slate-600">Loading...</div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg text-slate-600">Property not found</div>
      </div>
    );
  }

  const amenityIcons = {
    'WiFi': Wifi,
    'TV': Tv,
    'Coffee Machine': Coffee,
    'Air Conditioning': Wind,
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="pt-20">
        {/* Single Hero Image */}
        <div className="w-full h-[500px]" data-testid="property-gallery">
          <img 
            src={property.images[0]}
            alt="Property main view"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Property Details */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <h1 className="text-4xl md:text-5xl font-heading font-bold text-primary mb-4" data-testid="property-title">
                  {property.name}
                </h1>
                <p className="text-lg text-slate-600 mb-2">{property.location}</p>
                <p className="text-base text-slate-500 mb-8">Up to {property.max_guests} guests</p>

                <div className="prose max-w-none mb-12">
                  <h2 className="text-2xl font-heading font-semibold text-primary mb-4">
                    About This Space
                  </h2>
                  <p className="text-slate-600 leading-relaxed">
                    {property.description}
                  </p>
                </div>

                <div className="mb-12">
                  <h2 className="text-2xl font-heading font-semibold text-primary mb-6">
                    Amenities
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {property.amenities.map((amenity, idx) => {
                      const Icon = amenityIcons[amenity] || Check;
                      return (
                        <div key={idx} className="flex items-center gap-3" data-testid={`amenity-${idx}`}>
                          <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                            <Icon className="w-5 h-5 text-primary" />
                          </div>
                          <span className="text-slate-700">{amenity}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="bg-accent/10 border border-accent/20 rounded-lg p-6">
                  <div className="flex items-start gap-3">
                    <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="font-semibold text-primary mb-2">Best Price Guarantee</h3>
                      <p className="text-slate-600">
                        Book directly with us and save 12% compared to Airbnb. We guarantee the best rate.
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="lg:col-span-5">
              <GuestyWidget />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyDetails;